import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import {
  useGetFinixOnboardingOverviewQuery,
  useStartProviderOnboardingMutation,
  useReissueOnboardingLinkMutation,
} from "store/queries/finixAdmin";
import styled from "styled-components";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const ProviderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #dee2e6;

  &:last-child {
    border-bottom: none;
  }
`;

const ProviderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChecklistItem = styled.div<{ $passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: ${(props) => (props.$passed ? "#22c55e" : "#6c757d")};
`;

function getStatusSeverity(
  status: string | null
): "success" | "warning" | "danger" | "info" | undefined {
  if (!status) return undefined;
  switch (status) {
    case "APPROVED":
      return "success";
    case "PENDING":
    case "SUBMITTED":
      return "warning";
    case "REJECTED":
      return "danger";
    case "UPDATE_REQUESTED":
      return "warning";
    default:
      return "info";
  }
}

function getStatusLabel(status: string | null, hasMerchant: boolean): string {
  if (hasMerchant) return "Onboarded";
  if (!status) return "Not Started";
  switch (status) {
    case "PENDING":
      return "Pending";
    case "SUBMITTED":
      return "Submitted";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "UPDATE_REQUESTED":
      return "Update Requested";
    default:
      return status;
  }
}

export function FinixSettings({ accountId }: { accountId: string }) {
  const [expanded, setExpanded] = useState(true);
  const { data, isLoading, isError } =
    useGetFinixOnboardingOverviewQuery(accountId);
  const [startProviderOnboarding] = useStartProviderOnboardingMutation();
  const [reissueLink] = useReissueOnboardingLinkMutation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { current: toastElement } = useContext(ToastContext);

  const showError = (error: any, fallback: string) => {
    const message =
      error?.data?.message || error?.data?.messages?.[0] || fallback;
    toastElement?.show({
      severity: "error",
      summary: "Finix Error",
      detail: message,
      life: 6000,
    });
  };

  const handleStartOnboarding = async (providerId: string) => {
    try {
      await startProviderOnboarding(providerId).unwrap();
    } catch (error) {
      showError(error, "Failed to start onboarding. Please try again.");
    }
  };

  const handleReissueLink = async (formId: string) => {
    try {
      await reissueLink(formId).unwrap();
    } catch (error) {
      showError(error, "Failed to re-issue onboarding link. Please try again.");
    }
  };

  const handleCopyLink = async (url: string, providerId: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(providerId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard API may fail in non-HTTPS or restricted contexts
    }
  };

  if (isLoading) {
    return (
      <div className="block bg-white border-round p-4">
        <Title>Finix Payment Configuration</Title>
        <ProgressSpinner style={{ width: "30px", height: "30px" }} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="block bg-white border-round p-4">
        <Title>Finix Payment Configuration</Title>
        <Message
          severity="error"
          text="Failed to load Finix onboarding status."
          className="w-full"
        />
      </div>
    );
  }

  if (!data) return null;

  const { providers, readiness } = data;

  return (
    <div className="block bg-white border-round-xl p-4 border-1 border-gray-300">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Finix Payment Configuration
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>

      {expanded && (
        <>
          {/* Readiness Checklist */}
          <div className="mb-4 mt-4">
            <h4
              className="mt-0 mb-2"
              style={{ fontSize: "14px", fontWeight: 600 }}
            >
              Readiness Checklist
            </h4>
            <ChecklistItem $passed={readiness.facilityOnboarded}>
              <i
                className={
                  readiness.facilityOnboarded
                    ? "pi pi-check-circle"
                    : "pi pi-circle"
                }
              />
              <span>Main facility onboarded with Finix</span>
            </ChecklistItem>
            <ChecklistItem $passed={readiness.allProvidersOnboarded}>
              <i
                className={
                  readiness.allProvidersOnboarded
                    ? "pi pi-check-circle"
                    : "pi pi-circle"
                }
              />
              <span>All providers onboarded with Finix</span>
            </ChecklistItem>
            <ChecklistItem
              $passed={readiness.activePaymentPlansNeedingMigration === 0}
            >
              <i
                className={
                  readiness.activePaymentPlansNeedingMigration === 0
                    ? "pi pi-check-circle"
                    : "pi pi-circle"
                }
              />
              <span>
                Active payment plans migrated
                {readiness.activePaymentPlansNeedingMigration > 0 &&
                  ` (${readiness.activePaymentPlansNeedingMigration} remaining)`}
              </span>
            </ChecklistItem>
          </div>

          {!readiness.ready && (
            <StyledMessage
              severity="warn"
              text="Complete all checklist items before enabling Finix for this account."
              className="mb-4 w-full"
            />
          )}

          {/* Provider Onboarding Status */}
          <h4
            className="mt-0 mb-2"
            style={{ fontSize: "14px", fontWeight: 600 }}
          >
            Provider Onboarding
          </h4>
          <div>
            {providers.map((provider) => (
              <ProviderRow key={provider.id}>
                <ProviderInfo>
                  <div>
                    <span style={{ fontWeight: 500 }}>{provider.name}</span>
                    {provider.mainFacility && (
                      <Tag
                        value="Facility"
                        severity="info"
                        className="ml-2"
                        style={{
                          fontSize: "10px",
                          backgroundColor: "var(--primary-color)",
                        }}
                      />
                    )}
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      {provider.specialty}
                      {provider.finixMerchantId && (
                        <span className="ml-2">
                          Merchant: {provider.finixMerchantId}
                        </span>
                      )}
                    </div>
                  </div>
                </ProviderInfo>

                <div className="flex align-items-center gap-2">
                  <Tag
                    value={getStatusLabel(
                      provider.onboardingStatus,
                      !!provider.finixMerchantId
                    )}
                    severity={getStatusSeverity(
                      provider.finixMerchantId
                        ? "APPROVED"
                        : provider.onboardingStatus
                    )}
                  />

                  {/* Action buttons based on status */}
                  {!provider.finixMerchantId && !provider.onboardingStatus && (
                    <Button
                      label="Start Onboarding"
                      className="p-button-sm p-button-outlined"
                      onClick={() => handleStartOnboarding(provider.id)}
                    />
                  )}

                  {!provider.finixMerchantId &&
                    provider.onboardingStatus === "PENDING" &&
                    provider.onboardingFormId && (
                      <>
                        {provider.onboardingUrl && (
                          <Button
                            label={
                              copiedId === provider.id ? "Copied!" : "Copy Link"
                            }
                            className="p-button-sm p-button-outlined"
                            icon="pi pi-copy"
                            onClick={() =>
                              handleCopyLink(
                                provider.onboardingUrl!,
                                provider.id
                              )
                            }
                          />
                        )}
                        <Button
                          label="Re-issue Link"
                          className="p-button-sm p-button-text"
                          onClick={() =>
                            handleReissueLink(provider.onboardingFormId!)
                          }
                        />
                      </>
                    )}

                  {!provider.finixMerchantId &&
                    (provider.onboardingStatus === "UPDATE_REQUESTED" ||
                      provider.onboardingStatus === "SUBMITTED") &&
                    provider.onboardingFormId && (
                      <Button
                        label="Re-issue Link"
                        className={`p-button-sm p-button-outlined${
                          provider.onboardingStatus === "UPDATE_REQUESTED"
                            ? " p-button-warning"
                            : ""
                        }`}
                        onClick={() =>
                          handleReissueLink(provider.onboardingFormId!)
                        }
                      />
                    )}

                  {!provider.finixMerchantId &&
                    provider.onboardingStatus === "REJECTED" && (
                      <Button
                        label="Restart Onboarding"
                        className="p-button-sm p-button-outlined p-button-danger"
                        onClick={() => handleStartOnboarding(provider.id)}
                      />
                    )}
                </div>
              </ProviderRow>
            ))}

            {providers.length === 0 && (
              <p style={{ color: "#6c757d" }}>
                No providers found for this account.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
